import { z } from 'zod';
import { nullableOptional } from '../../helpers/zod';
// We're using zod here because it provides runtime type validation. You will see runtime errors if you attempt
// to set user state with invalid values. Type validation is important here because the state we write to the larger parabola
// model is persisted between sessions and is read by other systems. Parabola's user model is BC's window to the outside world!
const DependentSchema = z.object({
    age: z.nullable(z.number()),
    relationship: z.nullable(z.string()),
});
const SpouseTypeSchema = nullableOptional(z.enum(['Spouse', 'SSDP', 'None'])); // should never be null or undefined by the time the user hits a storybook module
const SelectionStatusSchema = nullableOptional(z.enum(['Selected', 'Waived'])); // Set when the user confirms their decision about their plan coverage
const CoverageTierSchema = nullableOptional(z.enum(['t1', 't2', 't3', 't4', 't5', 't6', 't7', 't8', 't9', 't10'])); // The coverage tier the user has selected for their plan
const UserPlanBaseSchema = z.object({
    selectionStatus: SelectionStatusSchema,
    selectedPlanId: nullableOptional(z.number()), // The Builder ID of the selected plan. Not set when the user waives coverage.
    seenPlanExp: nullableOptional(z.boolean()), // Has the user completed the module's plan explanation content? (Set to true when the user clicks any button in the EXP - decision prompt page)
});
const UserPlanCoverDepsBaseSchema = UserPlanBaseSchema.extend({
    coverKidsNum: nullableOptional(z.number()), // The number of kids the user said they'll cover under their plan
    coverSpouse: nullableOptional(z.boolean()), // Is the user covering their spouse or eligible partner under their plan?
    coveredMembersDisplayText: nullableOptional(z.string()), // Text about who the user is covering on their plan for display on screen to the end user. Logic in parabola, saved to usermodel via Kentico set user state
    eligibleKidsNum: nullableOptional(z.number()), // The number of kids that are eligible for the plan benefits. Logic in parabola, saved to usermodel via Kentico set user state
    hasEligibleSignificantOther: nullableOptional(z.boolean()), // Is the user's spouse/partner eligible for the plan's benefits? Logic in parabola, saved to usermodel via Kentico set user state
    Election: CoverageTierSchema, // The coverage tier the user has selected for their plan
});
const UserStateSchema = z.object({
    Global: z.object({
        age: nullableOptional(z.number()),
        dependents: z.array(DependentSchema),
        spouseType: SpouseTypeSchema,
        numKids: nullableOptional(z.number()), // should always be at least 0 by the time the user hits a storybook module
        expectsMaternity: nullableOptional(z.boolean()),
        fromMedToTax: nullableOptional(z.number()), // Gets set to 1 in med outro if a user has chosen a med plan with a healthcare account. This allows us to send the user straight into HCA REC from med outro. (If the user enters any other module after medical, the variable turned off/set to 0)
        SeenOpening: z.object({
            hospitalIndemnity: nullableOptional(z.boolean()), // Has the user interacted with any of the Hospital Indemnity router menu pages? (OPEN 010, OPEN 020, OPEN 030, OPEN 040)
            pet: nullableOptional(z.boolean()), // Has the user interacted with any of the Hospital Indemnity router menu pages? (OPEN 010, OPEN 020, OPEN 030, OPEN 040)
            cancer: nullableOptional(z.boolean()),
        }),
    }),
    HI: UserPlanCoverDepsBaseSchema.extend({
        wantsCoverage: nullableOptional(z.boolean()), // Is the user interested in HI coverage? (can be set in Med Outro as well as during the HI module)
    }),
    cancer: UserPlanCoverDepsBaseSchema.extend({}),
    pet: UserPlanBaseSchema.extend({}),
    parabolaModel: z.object({
        eligibleModules: z.optional(z.array(z.string())),
        completedModules: z.optional(z.array(z.string())),
        storybookModules: z.optional(z.object({
            hospital: nullableOptional(z.string()), // Either "Hospital Indemnity" or the custom module name from Builder
            pet: nullableOptional(z.string()), // Either "Pet Insurance" or the custom module name from Builder
            cancer: nullableOptional(z.string()), // Either "cancer Insurance" or the custom module name from Builder
        })),
    }),
});
export const PartialUserStateSchema = UserStateSchema.deepPartial();
//# sourceMappingURL=user.js.map